import { type CSSProperties, type RefObject, useLayoutEffect, useState } from 'react';

import { findScrollParent } from './find-scroll-parent.js';

const BOTTOM_GUTTER_PX = 24;

function nodesThatMoveTheElement(element: HTMLElement, scroller: HTMLElement | null) {
  const nodes = new Set<Element>([document.documentElement]);
  for (
    let node: HTMLElement | null = element;
    node && node !== scroller && node !== document.body;
    node = node.parentElement
  ) {
    if (node.parentElement) nodes.add(node.parentElement);
    for (
      let sibling = node.previousElementSibling;
      sibling;
      sibling = sibling.previousElementSibling
    )
      nodes.add(sibling);
  }
  if (scroller) nodes.add(scroller);
  return nodes;
}

export function useFillViewportHeight(
  elementRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  minHeightPx: number,
): CSSProperties | undefined {
  const [top, setTop] = useState(0);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!enabled || !element) return;
    const scroller = findScrollParent(element);
    // Scroll offsets turn the viewport-relative rect into the element's top at scroll zero.
    const measure = () =>
      setTop(element.getBoundingClientRect().top + (scroller?.scrollTop ?? 0) + window.scrollY);
    measure();
    window.addEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    for (const node of nodesThatMoveTheElement(element, scroller)) observer.observe(node);
    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [elementRef, enabled]);

  if (!enabled) return undefined;
  return {
    height: `calc(100vh - ${top + BOTTOM_GUTTER_PX}px)`,
    minHeight: `${minHeightPx}px`,
  };
}
